// @ts-ignore no types, package too old :(
import { push } from 'react-router-redux';

// Utils
import { createQueryString } from '../../../common/utils/stringUtil';
import { getAliasPermissionId, getLinkedElementId } from '../../../common/elements/utils/elementPropertyUtils';
import {
    getClosestAncestor,
    hasRetrievedAllAncestors,
    isElementOrAncestorInTrash,
} from '../../../common/elements/utils/elementTraversalUtils';
import { isBoard, isSkeleton } from '../../../common/elements/utils/elementTypeUtils';
import { hasReadPermission } from '../../../common/permissions/elementPermissionsUtil';

// Services
import { fetchBoards } from '../board/boardService';

// Actions
import { openPopup } from '../../components/popupPanel/popupActions';
import { deselectAllElements } from '../selection/selectionActions';

// Selectors
import { getElements } from '../selectors/elementsSelector';
import {
    getAclIdsSelector,
    getClosestPermissionIdForElementIdSelector,
} from '../../utils/permissions/permissionsSelector';

// Constants
import { PopupIds } from '../../components/popupPanel/popupConstants';
import { APP_QUERY_PARAMS } from '../../../common/utils/urlConstants';
import { ELEMENT_FILTER_CONTEXTS } from '../elementFilter/elementFilterConstants';

// Unreadable ancestors arrive as skeletons, so stopping at them prevents walking
// past a hidden parent board to a readable ancestor further up.
const getClosestAncestorBoardOrSkeletonId = getClosestAncestor(
    (element: unknown) => isBoard(element) || isSkeleton(element),
);

/**
 * Navigates to the board containing a shortcut's original board and highlights the
 * original via the element filter, mirroring search-result navigation. Unlike the
 * search context, clicking away afterwards intentionally clears the highlight.
 * Shows an error toast when that containing board can't be read (or doesn't exist,
 * e.g. the original is a root board).
 */
export const navigateToAliasOriginalBoard =
    (aliasElementId: string) =>
    async (dispatch: Function, getState: Function): Promise<void> => {
        const aliasElement = getElements(getState()).get(aliasElementId);
        const originalBoardId = getLinkedElementId(aliasElement);
        if (!originalBoardId) return;

        const aliasPermissionId = getAliasPermissionId(aliasElement);

        // The original board and its ancestors load lazily, so they might not be in the store yet.
        // Forced, because the board can be marked as fetched by a fetch that skipped ancestors.
        if (!hasRetrievedAllAncestors(getElements(getState()), originalBoardId)) {
            try {
                await dispatch(fetchBoards({ boardIds: [originalBoardId], loadAncestors: true, force: true }));
            } catch {
                // Fall through — the guard below shows the error toast
            }
        }

        const state = getState();
        const elements = getElements(state);
        const parentBoardId = getClosestAncestorBoardOrSkeletonId(elements, originalBoardId);

        const canShowOriginal =
            !!parentBoardId &&
            isBoard(elements.get(parentBoardId)) &&
            isBoard(elements.get(originalBoardId)) &&
            !isElementOrAncestorInTrash(elements, originalBoardId) &&
            hasReadPermission(elements, parentBoardId, getAclIdsSelector(state));

        if (!canShowOriginal) {
            dispatch(openPopup({ popupId: PopupIds.FIND_ORIGINAL_BOARD_ERROR }));
            return;
        }

        const getClosestPermissionId = getClosestPermissionIdForElementIdSelector() as (
            state: unknown,
            props: { elementId: string },
        ) => string | undefined;

        const queryString = createQueryString({
            [APP_QUERY_PARAMS.FILTER_IDS]: originalBoardId,
            [APP_QUERY_PARAMS.FILTER_CONTEXT]: ELEMENT_FILTER_CONTEXTS.ALIAS,
            // Kept on the URL so a refresh on the destination retains permission-link access
            [APP_QUERY_PARAMS.PERMISSION]:
                aliasPermissionId || getClosestPermissionId(state, { elementId: parentBoardId }),
        });

        // Arriving with the alias still selected would make the filter's focus selection
        // deselect it, which the filter middleware treats as a user deselect and clears
        // the freshly applied element filter
        dispatch(deselectAllElements());
        dispatch(push(`/${parentBoardId}?${queryString}`));
    };
